import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '../contexts/auth-context'
import { getCompanyId, getApiUrl } from '../lib/config'
import { purchaseApi, companiesApi, type Vendor, type PurchaseOrderLine } from '../lib/api/accounting'
import { inventoryApi } from '../lib/api/inventory'
import { formatCurrency } from '../lib/utils'

type OrderStatus = 'draft' | 'approved' | 'delivered' | 'closed' | 'cancelled';

interface PurchaseOrder {
  id: string;
  companyId: string;
  company?: Company;
  vendorId: string;
  vendor?: Vendor;
  poNumber: string;
  orderDate: string;
  expectedDelivery?: string;
  orderSource: 'internal' | 'external';
  currency: string;
  notes?: string;
  status: OrderStatus;
  totalAmount: number;
  inventoryType: 'assets' | 'fixed_assets';
  lines: PurchaseOrderLine[];
  createdAt?: string;
  updatedAt?: string;
}

interface Product {
  id: string;
  name: string;
  sku: string;
  description?: string;
  unitPrice: number;
  costPrice: number;
  stockQuantity: number;
  status: 'ACTIVE' | 'INACTIVE' | 'DISCONTINUED' | 'DRAFT';
  reorderPoint: number;
}
import { PageLayout } from '../components/page-layout'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Badge } from '../components/ui/badge'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form'
import { Textarea } from '../components/ui/textarea'
import { MoonLoader } from '../components/ui/moon-loader'
import { Separator } from '../components/ui/separator'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useToast } from '../hooks/use-toast'
import { 
  Plus, 
  Search, 
  Eye, 
  Edit, 
  Trash2, 
  FileText, 
  Download,
  Send,
  DollarSign,
  Package,
  Truck,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  Building,
  CreditCard,
  Calculator,
  Tag
} from 'lucide-react'
import { format } from 'date-fns'
import { VendorModal } from '../components/vendor-modal'
import { getTenantId } from '../lib/config'
import { TranslatedText } from '../components/translated-text'
import { useTranslation } from '../hooks/useTranslation'

// Types imported from API

interface Company {
  id: string
  name: string
}

// Function to format purchase order notes with translations
function formatPurchaseOrderNotes(notes: string, tSync: (key: string, fallback?: string) => string): React.ReactNode[] {
  if (!notes) return []
  
  const lines = notes.split('\n').filter(line => line.trim())
  const formatted: React.ReactNode[] = []
  
  lines.forEach((line, index) => {
    const trimmedLine = line.trim()
    
    // Check for activity markers
    if (trimmedLine.startsWith('[ACTIVITY:DELIVERED]')) {
      const data = trimmedLine.replace('[ACTIVITY:DELIVERED]', '')
      const [date, deliveredBy] = data.split('|')
      
      // Format date nicely
      let formattedDate = date
      try {
        const dateObj = new Date(date)
        if (!isNaN(dateObj.getTime())) {
          formattedDate = format(dateObj, 'MMM dd, yyyy')
        }
      } catch (e) {
        // Keep original date if parsing fails
      }
      
      const deliveredByText = deliveredBy === 'System' 
        ? tSync('System', 'System')
        : deliveredBy || tSync('System', 'System')
      
      formatted.push(
        <div key={index} className="flex items-start gap-2 text-sm">
          <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{tSync('Delivered', 'Delivered')}</strong> {tSync('on', 'on')} <strong>{formattedDate}</strong> {tSync('by', 'by')} <strong>{deliveredByText}</strong>
          </span>
        </div>
      )
    } else if (trimmedLine.startsWith('[ACTIVITY:PAID]')) {
      const paymentRef = trimmedLine.replace('[ACTIVITY:PAID]', '')
      formatted.push(
        <div key={index} className="flex items-start gap-2 text-sm">
          <CreditCard className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <span>
            <strong>{tSync('Marked as paid', 'Marked as paid')}</strong> {tSync('via payment', 'via payment')} <strong>{paymentRef}</strong>
          </span>
        </div>
      )
    } else if (trimmedLine) {
      // Regular note text
      formatted.push(
        <div key={index} className="text-base text-gray-700">
          {trimmedLine}
        </div>
      )
    }
  })
  
  return formatted
}

// Validation schemas
const purchaseOrderSchema = z.object({
  companyId: z.string().min(1, 'Company is required'),
  vendorId: z.string().min(1, 'Vendor is required'),
  poNumber: z.string().min(1, 'PO Number is required'),
  orderDate: z.string().min(1, 'Order date is required'),
  expectedDelivery: z.string().optional(),
  orderSource: z.enum(['internal', 'external'], {
    required_error: 'Order source is required',
  }),
  currency: z.string().default('USD'),
  notes: z.string().optional(),
  // Inventory Type - determines how products affect inventory
  inventoryType: z.enum(['assets', 'fixed_assets']).default('assets'),
  lines: z.array(z.object({
    productId: z.string().optional(),
    description: z.string().min(1, 'Description is required'),
    quantity: z.coerce.number().positive('Quantity must be positive'),
    unitPrice: z.coerce.number().nonnegative('Unit price must be non-negative'),
    taxRate: z.coerce.number().nonnegative().default(0)
  })).min(1, 'At least one line item is required')
})

export default function PurchaseOrdersPage() {
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const { toast } = useToast()
  const { tSync } = useTranslation()
  const [companyId, setCompanyId] = useState<string>(getCompanyId())
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isViewOpen, setIsViewOpen] = useState(false)

  // Listen for company changes from header
  useEffect(() => {
    const handleStorageChange = () => {
      const newCompanyId = getCompanyId();
      if (newCompanyId && newCompanyId !== companyId) {
        setCompanyId(newCompanyId);
      }
    };

    // Listen for localStorage changes
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events (in case localStorage doesn't trigger)
    const handleCompanyChange = (e: CustomEvent) => {
      const newCompanyId = e.detail.companyId;
      if (newCompanyId && newCompanyId !== companyId) {
        setCompanyId(newCompanyId);
      }
    };
    
    window.addEventListener('companyChanged', handleCompanyChange as EventListener);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('companyChanged', handleCompanyChange as EventListener);
    };
  }, [companyId]);

  
  const [selectedOrder, setSelectedOrder] = useState<PurchaseOrder | null>(null)
  const [editingOrder, setEditingOrder] = useState<PurchaseOrder | null>(null)
  const [currentInventoryType, setCurrentInventoryType] = useState<'assets' | 'fixed_assets'>('assets')
  const [loadingStates, setLoadingStates] = useState<{[key: string]: boolean}>({})
  const [approvingOrderId, setApprovingOrderId] = useState<string | null>(null)
  const [deliveringOrderId, setDeliveringOrderId] = useState<string | null>(null)
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null)
  const [closingOrderId, setClosingOrderId] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize] = useState(10)
  const [isVendorModalOpen, setIsVendorModalOpen] = useState(false)
  
  // Reset pagination when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, searchTerm]);
  
  const queryClient = useQueryClient()

  // Fetch products (only ACTIVE products for purchase orders)
  const { data: products } = useQuery({
    queryKey: ['products', 'active'],
    queryFn: async () => {
      const response = await inventoryApi.getProducts({
        companyId: getCompanyId(),
        status: 'ACTIVE' // Only fetch active products
      })
      return response.items
    },
    enabled: isAuthenticated && !authLoading
  })

  // Fetch fixed assets for fixed assets inventory type
  const { data: fixedAssets } = useQuery({
    queryKey: ['fixed-assets', companyId],
    queryFn: async () => {
      const response = await fetch(`${getApiUrl('')}/api/fixed-assets?companyId=${companyId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'x-tenant-id': localStorage.getItem('tenant_id') || 'tenant_demo',
          'x-company-id': companyId,
        },
      });
      const data = await response.json();
      return data.assets || [];
    },
    enabled: isAuthenticated && !authLoading && !!companyId
  })

  // Fetch data
  const { data: companiesData } = useQuery<Company[]>({
    queryKey: ['companies'],
    queryFn: async () => await companiesApi.getCompanies(),
    enabled: isAuthenticated && !authLoading
  })
  
  // Get all companies
  const companies = React.useMemo(() => {
    if (!companiesData) return [];
    return Array.isArray(companiesData) ? companiesData : [];
  }, [companiesData])

  const { data: vendors } = useQuery<Vendor[]>({
    queryKey: ['vendors', companyId],
    queryFn: async () => {
      const result = await purchaseApi.getVendors(companyId);
      // API service now handles data extraction
      return result;
    },
    enabled: isAuthenticated && !authLoading
  })

  const { data: purchaseOrdersData, isLoading: ordersLoading } = useQuery<{orders: PurchaseOrder[], pagination: any}>({
    queryKey: ['purchase-orders', statusFilter, currentPage, pageSize, searchTerm],
    queryFn: async () => {
      try {
        const response = await purchaseApi.getPurchaseOrders(
          companyId,
          statusFilter === 'all' ? undefined : statusFilter as any,
          currentPage,
          pageSize,
          searchTerm || undefined
        );
        return response as any;
      } catch (error) {
        console.error('Error fetching purchase orders:', error);
        throw error;
      }
    },
    enabled: isAuthenticated && !authLoading
  })

  const purchaseOrders = purchaseOrdersData?.orders || []
  const pagination = purchaseOrdersData?.pagination

  // Fetch individual purchase order details
  const { data: orderDetails, isLoading: orderDetailsLoading } = useQuery<PurchaseOrder>({
    queryKey: ['purchase-order', selectedOrder?.id],
    enabled: !!selectedOrder?.id,
    queryFn: async () => {
      const response = await purchaseApi.getPurchaseOrderById(selectedOrder!.id)
      return response as any as PurchaseOrder
    }
  })

  // Mutations
  const createOrder = useMutation({
    mutationFn: async (data: any) => purchaseApi.createPurchaseOrder(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
      setIsCreateOpen(false)
      toast({
        title: 'Success',
        description: 'Purchase order created successfully'
      })
    },
    onError: (error: any) => {
      // Prioritize message field for clearer error display
      const errorMessage = error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to create purchase order'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      })
    }
  })

  const updateOrder = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: any }) => {
      try {
        const response = await purchaseApi.updatePurchaseOrder(id, data);
        await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
        return response;
      } catch (error) {
        console.error('Error in updateOrder mutation:', error);
        throw error;
      }
    },
    onSuccess: (response, variables) => {
      setIsCreateOpen(false)
      setEditingOrder(null)
      
      // Show appropriate success message based on status change
      const newStatus = variables.data.status;
      let successMessage = 'Purchase order updated successfully';
      
      switch (newStatus) {
        case 'approved':
          successMessage = 'Purchase order approved';
          break;
        default:
          successMessage = 'Purchase order updated successfully';
      }
      
      toast({
        title: 'Success',
        description: successMessage
      })
    },
    onError: (error: any) => {
      console.error('Purchase order update failed:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to update purchase order'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      })
    }
  })

  const deleteOrder = useMutation({
    mutationFn: async (id: string) => purchaseApi.deletePurchaseOrder(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
      toast({
        title: 'Success',
        description: 'Purchase order deleted successfully',
      })
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to delete purchase order'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      })
    }
  })

  // Create vendor mutation
  const createVendorMutation = useMutation({
    mutationFn: async (vendorData: any) => {
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token')
      const tenantId = getTenantId()
      const response = await fetch(
        getApiUrl('api/enhanced-transaction-processing/vendors'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': companyId
          },
          body: JSON.stringify(vendorData)
        }
      )
      const data = await response.json()
      if (!data.success) {
        // Handle duplicate vendor error specifically
        if (data.error === 'Vendor already exists' || data.message?.includes('already exists')) {
          throw new Error(data.message || 'A vendor with this name already exists for this company.')
        }
        throw new Error(data.message || data.error || 'Failed to create vendor')
      }
      return data.data
    },
    onSuccess: (newVendor) => {
      // Invalidate vendors query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['vendors', companyId] })
      setIsVendorModalOpen(false)
      // Select the newly created vendor
      form.setValue('vendorId', newVendor.id)
      toast({
        title: 'Success',
        description: 'Vendor created successfully',
      })
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to create vendor'
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      })
    }
  })

  // Get active company from header (localStorage)
  const activeCompanyId = React.useMemo(() => {
    try {
      return getCompanyId();
    } catch {
      return companies?.[0]?.id || '';
    }
  }, [companies]);

  // Get active company name
  const activeCompanyName = React.useMemo(() => {
    if (!activeCompanyId) return '';
    const company = companies.find((c: Company) => c.id === activeCompanyId);
    return company?.name || '';
  }, [activeCompanyId, companies]);

  // Form setup
  const form = useForm({
    resolver: zodResolver(purchaseOrderSchema),
    defaultValues: {
      companyId: activeCompanyId, // Use company from header
      vendorId: '',
      poNumber: '',
      orderDate: '',
      expectedDelivery: '',
      currency: 'USD',
      notes: '',
      inventoryType: 'assets' as const,
      lines: [{ description: '', quantity: 1, unitPrice: 0, taxRate: 0 }]
    }
  })

  // Update form when active company changes
  useEffect(() => {
    if (activeCompanyId) {
      form.setValue('companyId', activeCompanyId);
    }
  }, [activeCompanyId, form])

  // Clear product selections when inventory type changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'inventoryType') {
        const newInventoryType = value.inventoryType as 'assets' | 'fixed_assets';
        setCurrentInventoryType(newInventoryType);
        
        // Clear all product selections when inventory type changes
        const currentLines = form.getValues('lines') || [];
        const clearedLines = currentLines.map(line => ({
          ...line,
          productId: '',
          description: '',
          unitPrice: 0
        }));
        form.setValue('lines', clearedLines);
        
        // Force form re-render
        form.trigger();
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // Live totals
  const watchedLines = form.watch('lines') as Array<{ quantity?: number; unitPrice?: number; taxRate?: number }>
  const totals = (Array.isArray(watchedLines) ? watchedLines : []).reduce((acc, l) => {
    const qty = Number(l?.quantity || 0)
    const price = Number(l?.unitPrice || 0)
    const line = qty * price
    const tax = line * (Number(l?.taxRate || 0) / 100)
    acc.subtotal += line
    acc.tax += tax
    acc.total += line + tax
    return acc
  }, { subtotal: 0, tax: 0, total: 0 })

  const onSubmit = (data: any) => {
    // Validate that all lines have required fields
    const validatedLines = data.lines.map((line: any) => {
      if (!line.description || !line.quantity || !line.unitPrice) {
        throw new Error('All line items must have description, quantity, and unit price')
      }
      
      if (data.inventoryType === 'assets' && !line.productId) {
        throw new Error('Product selection is required for inventory items')
      }
      
      const processedLine = {
        ...line,
        // For fixed assets, ensure productId is null/undefined
        productId: data.inventoryType === 'fixed_assets' ? undefined : line.productId,
        // Remove assetId from the final data as it's not needed by the backend
        assetId: undefined
      };
      
      return processedLine;
    })

    // Convert date strings to datetime format for backend
    const transformedData = {
      ...data,
      lines: validatedLines,
      orderDate: data.orderDate ? new Date(data.orderDate).toISOString() : undefined,
      expectedDelivery: data.expectedDelivery ? new Date(data.expectedDelivery).toISOString() : undefined
    }
    
    if (editingOrder) {
      updateOrder.mutate({ id: editingOrder.id, data: transformedData })
    } else {
      createOrder.mutate(transformedData)
    }
  }

  const getStatusColor = (status: OrderStatus) => {
    switch (status) {
      case 'draft': return 'secondary'
      case 'approved': return 'outline'
      case 'delivered': return 'outline'
      case 'closed': return 'secondary'
      case 'cancelled': return 'destructive'
      default: return 'secondary'
    }
  }

  const getStatusBadgeClassName = (status: OrderStatus) => {
    switch (status) {
      case 'approved':
        return '!bg-teal-50 !text-teal-700 !border-teal-200 hover:!bg-teal-100'
      case 'delivered':
        return '!bg-blue-50 !text-blue-700 !border-blue-200 hover:!bg-blue-100'
      default:
        return ''
    }
  }

  const getStatusIcon = (status: OrderStatus) => {
    switch (status) {
      case 'draft': return <FileText className="w-4 h-4" />
      case 'approved': return <CheckCircle className="w-4 h-4" />
      case 'delivered': return <Package className="w-4 h-4" />
      case 'closed': return <CheckCircle className="w-4 h-4" />
      case 'cancelled': return <XCircle className="w-4 h-4" />
      default: return <Clock className="w-4 h-4" />
    }
  }

  // Use paginated data directly instead of client-side filtering
  const displayOrders = purchaseOrders || []

  /**
   * Download purchase order as PDF
   * 
   * @param purchaseOrderId - ID of the purchase order to download
   * @param poNumber - PO number for the filename
   */
  const handleDownloadPDF = async (purchaseOrderId: string, poNumber: string) => {
    try {
      const tenantId = localStorage.getItem('tenant_id') || 'tenant_demo'
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token')

      if (!token) {
        toast({
          title: 'Error',
          description: 'Authentication required. Please log in.',
          variant: 'destructive'
        })
        return
      }

      const loadingToast = toast({
        title: 'Generating PDF...',
        description: 'Please wait while we generate your PDF',
      })

      const response = await fetch(
        getApiUrl(`api/purchase-orders/${purchaseOrderId}/pdf`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': companyId,
            'Content-Type': 'application/json',
          },
        }
      )

      if (!response.ok) {
        const errorText = await response.text()
        console.error('PDF generation error:', response.status, errorText)
        loadingToast.dismiss()
        throw new Error(`Failed to generate PDF: ${response.status} ${response.statusText}`)
      }

      // Get the PDF blob
      const blob = await response.blob()
      
      // Create a download link
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `PO-${poNumber}.pdf`
      document.body.appendChild(link)
      link.click()
      
      // Cleanup
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      loadingToast.dismiss()
      toast({
        title: 'Success',
        description: 'PDF downloaded successfully',
      })
    } catch (error: any) {
      console.error('Error downloading PDF:', error)
      toast({
        title: 'Error',
        description: error?.message || 'Failed to download PDF',
        variant: 'destructive'
      })
    }
  }

  /**
   * Send purchase order to vendor via email
   * 
   * @param purchaseOrderId - ID of the purchase order to send
   * @param vendorName - Vendor name
   */
  const handleSendToVendor = async (purchaseOrderId: string, vendorName: string) => {
    try {
      const tenantId = localStorage.getItem('tenant_id') || 'tenant_demo'
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token')

      if (!token) {
        toast({
          title: 'Error',
          description: 'Authentication required. Please log in.',
          variant: 'destructive'
        })
        return
      }

      // Optional: Ask for additional notes
      const notes = typeof window !== 'undefined' ? 
        window.prompt(`Send to ${vendorName}?\n\nOptional notes (leave blank if none):`) : 
        null

      if (notes === null) {
        // User cancelled
        return
      }

      const loadingToast = toast({
        title: 'Sending...',
        description: `Sending purchase order to ${vendorName}...`,
      })

      const response = await fetch(
        getApiUrl(`api/purchase-orders/${purchaseOrderId}/send-to-vendor`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': companyId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ notes: notes || undefined })
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Send to vendor error:', response.status, errorData)
        loadingToast.dismiss()
        throw new Error(errorData.message || `Failed to send: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      
      loadingToast.dismiss()
      toast({
        title: 'Success',
        description: result.message || `Purchase order sent to ${vendorName}`,
      })
      
      // Refresh the purchase orders list to show updated status
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
    } catch (error: any) {
      console.error('Error sending to vendor:', error)
      toast({
        title: 'Error',
        description: error?.message || 'Failed to send to vendor',
        variant: 'destructive'
      })
    }
  }

  /**
   * Handle marking purchase order as delivered
   */
  const handleMarkAsDelivered = async (order: PurchaseOrder) => {
    try {
      const tenantId = localStorage.getItem('tenant_id') || 'tenant_demo'
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token')

      if (!token) {
        toast({
          title: 'Authentication Required',
          description: 'Please log in to continue',
          variant: 'destructive'
        })
        return
      }

      // Confirm the action
      const confirmed = typeof window !== 'undefined' ? 
        window.confirm(`Mark Purchase Order ${order.poNumber} as delivered?\n\nThis will:\n- Update stock levels\n- Create journal entries\n- Mark the order as delivered`) : 
        false

      if (!confirmed) {
        return
      }

      // Show loading toast and track its ID
      const loadingToast = toast({
        title: 'Processing...',
        description: `Marking ${order.poNumber} as delivered...`
      })

      // Prepare delivery data
      const deliveryData = {
        deliveredDate: new Date().toISOString(),
        deliveredBy: 'System', // TODO: Get actual user name
        notes: `Marked as delivered on ${new Date().toLocaleDateString()}`,
        journalEntryData: {
          memo: `Inventory delivered from PO ${order.poNumber}`,
          reference: `PO-${order.poNumber}-DELIVERED`
        }
      }

      // Call the delivery API
      const response = await fetch(
        getApiUrl(`api/purchase-orders/${order.id}/deliver`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': companyId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(deliveryData)
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        console.error('Mark as delivered error:', response.status, errorData)
        // Dismiss loading toast before showing error
        loadingToast.dismiss()
        throw new Error(errorData.message || `Failed to mark as delivered: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      
      // Dismiss loading toast before showing success
      loadingToast.dismiss()
      
      // Show success notification
      toast({
        title: 'Success',
        description: result.message || `Purchase order ${order.poNumber} marked as delivered successfully`
      })
      
      // Refresh the purchase orders list to show updated status
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
    } catch (error: any) {
      console.error('Error marking as delivered:', error)
      toast({
        title: 'Error',
        description: error?.message || 'Failed to mark as delivered',
        variant: 'destructive'
      })
    }
  }

  /**
   * Handle approving purchase order
   */
  const handleApproveOrder = async (order: PurchaseOrder) => {
    setApprovingOrderId(order.id)
    try {
      const companyId = order.companyId || order.company?.id
      const vendorId = order.vendorId
      
      if (!companyId || !vendorId) {
        toast({
          title: 'Error',
          description: 'Missing required company or vendor information',
          variant: 'destructive'
        })
        return
      }

      const orderDate = order.orderDate 
        ? new Date(order.orderDate).toISOString() 
        : new Date().toISOString()

      await updateOrder.mutateAsync({
        id: order.id,
        data: {
          status: 'approved',
          companyId: companyId,
          vendorId: vendorId,
          poNumber: order.poNumber,
          orderDate: orderDate,
          expectedDelivery: order.expectedDelivery 
            ? new Date(order.expectedDelivery).toISOString() 
            : undefined,
          orderSource: order.orderSource || 'external',
          currency: order.currency || 'USD',
          notes: order.notes || '',
          lines: order.lines.map(line => ({
            id: line.id,
            productId: line.productId || '',
            description: line.description || '',
            quantity: Number(line.quantity) || 1,
            unitPrice: Number(line.unitPrice) || 0,
            taxRate: Number(line.taxRate) || 0
          }))
        }
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to approve purchase order',
        variant: 'destructive'
      })
    } finally {
      setApprovingOrderId(null)
    }
  }

  /**
   * Handle marking purchase order as delivered
   */
  const handleDeliverOrder = async (order: PurchaseOrder) => {
    setDeliveringOrderId(order.id)
    try {
      const deliveryData = {
        deliveredDate: new Date().toISOString(),
        deliveredBy: 'System',
        notes: `Marked as delivered on ${new Date().toLocaleDateString()}`,
        journalEntryData: {
          memo: `Inventory delivered from PO ${order.poNumber}`,
          reference: `PO-${order.poNumber}-DELIVERED`
        }
      }

      const apiCompanyId = getCompanyId()
      const tenantId = localStorage.getItem('tenant_id') || 'tenant_demo'
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token')

      const response = await fetch(
        getApiUrl(`api/purchase-orders/${order.id}/deliver`),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'x-tenant-id': tenantId,
            'x-company-id': apiCompanyId,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(deliveryData)
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || `Failed to deliver: ${response.status} ${response.statusText}`)
      }

      toast({
        title: 'Success',
        description: 'Purchase order delivered successfully!',
      })
      await queryClient.invalidateQueries({ queryKey: ['purchase-orders'] })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to deliver purchase order',
        variant: 'destructive'
      })
    } finally {
      setDeliveringOrderId(null)
    }
  }

  /**
   * Handle cancelling purchase order
   */
  const handleCancelOrder = async (order: PurchaseOrder) => {
    if (!confirm(`Are you sure you want to cancel purchase order ${order.poNumber}?`)) {
      return
    }

    setCancellingOrderId(order.id)
    try {
      const companyId = order.companyId || order.company?.id
      const vendorId = order.vendorId
      
      if (!companyId || !vendorId) {
        toast({
          title: 'Error',
          description: 'Missing required company or vendor information',
          variant: 'destructive'
        })
        return
      }

      const orderDate = order.orderDate 
        ? new Date(order.orderDate).toISOString() 
        : new Date().toISOString()

      await updateOrder.mutateAsync({
        id: order.id,
        data: {
          status: 'cancelled',
          companyId: companyId,
          vendorId: vendorId,
          poNumber: order.poNumber,
          orderDate: orderDate,
          expectedDelivery: order.expectedDelivery 
            ? new Date(order.expectedDelivery).toISOString() 
            : undefined,
          orderSource: order.orderSource || 'external',
          currency: order.currency || 'USD',
          notes: order.notes || '',
          lines: order.lines.map(line => ({
            id: line.id,
            productId: line.productId || '',
            description: line.description || '',
            quantity: Number(line.quantity) || 1,
            unitPrice: Number(line.unitPrice) || 0,
            taxRate: Number(line.taxRate) || 0
          }))
        }
      })
      toast({
        title: 'Success',
        description: 'Purchase order cancelled successfully',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to cancel purchase order',
        variant: 'destructive'
      })
    } finally {
      setCancellingOrderId(null)
    }
  }

  /**
   * Handle closing purchase order
   */
  const handleCloseOrder = async (order: PurchaseOrder) => {
    if (!confirm(`Are you sure you want to close purchase order ${order.poNumber}?`)) {
      return
    }

    setClosingOrderId(order.id)
    try {
      const companyId = order.companyId || order.company?.id
      const vendorId = order.vendorId
      
      if (!companyId || !vendorId) {
        toast({
          title: 'Error',
          description: 'Missing required company or vendor information',
          variant: 'destructive'
        })
        return
      }

      const orderDate = order.orderDate 
        ? new Date(order.orderDate).toISOString() 
        : new Date().toISOString()

      await updateOrder.mutateAsync({
        id: order.id,
        data: {
          status: 'closed',
          companyId: companyId,
          vendorId: vendorId,
          poNumber: order.poNumber,
          orderDate: orderDate,
          expectedDelivery: order.expectedDelivery 
            ? new Date(order.expectedDelivery).toISOString() 
            : undefined,
          orderSource: order.orderSource || 'external',
          currency: order.currency || 'USD',
          notes: order.notes || '',
          lines: order.lines.map(line => ({
            id: line.id,
            productId: line.productId || '',
            description: line.description || '',
            quantity: Number(line.quantity) || 1,
            unitPrice: Number(line.unitPrice) || 0,
            taxRate: Number(line.taxRate) || 0
          }))
        }
      })
      toast({
        title: 'Success',
        description: 'Purchase order closed successfully',
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to close purchase order',
        variant: 'destructive'
      })
    } finally {
      setClosingOrderId(null)
    }
  }

  /**
   * Export purchase orders to CSV
   */
  const exportPurchaseOrders = async () => {
    try {
      // Fetch all purchase orders with pagination
      const allPurchaseOrders: any[] = []
      let page = 1
      const pageSize = 100
      let hasMore = true

      while (hasMore) {
        const response = await purchaseApi.getPurchaseOrders(
          companyId,
          statusFilter === 'all' ? undefined : statusFilter as any,
          page,
          pageSize,
          searchTerm || undefined
        ) as any

        const ordersData = response?.orders || response?.items || response?.data || response
        const orders = Array.isArray(ordersData) ? ordersData : []

        if (orders.length === 0) {
          hasMore = false
        } else {
          allPurchaseOrders.push(...orders)
          
          if (orders.length < pageSize) {
            hasMore = false
          } else {
            page++
          }
        }
      }

      if (allPurchaseOrders.length === 0) {
        toast({
          title: 'Error',
          description: 'No purchase orders to export.',
          variant: 'destructive'
        })
        return
      }

      // Build export rows from purchase order lines
      const exportRows: any[] = []
      
      for (const order of allPurchaseOrders) {
        const orderLines = order.lines || []
        const orderDate = order.orderDate ? new Date(order.orderDate).toISOString().split('T')[0] : ''
        const poNumber = order.poNumber || order.id || ''
        const supplier = order.vendor?.name || order.vendorId || ''
        
        for (const line of orderLines) {
          const quantity = Number(line.quantity || 0)
          const unitPrice = Number(line.unitPrice || 0)
          const totalPrice = quantity * unitPrice
          
          // Get item category - check if product has category or use inventory type
          let itemCategory = ''
          if (line.productId) {
            // Try to get category from product if available
            const product = products?.find((p: any) => p.id === line.productId)
            // For now, use inventory type as category
            itemCategory = order.inventoryType === 'fixed_assets' ? 'Fixed Assets' : 'Inventory'
          } else {
            itemCategory = order.inventoryType === 'fixed_assets' ? 'Fixed Assets' : 'Inventory'
          }
          
          exportRows.push({
            date: orderDate,
            supplier: supplier,
            itemName: line.description || '',
            itemCategory: itemCategory,
            invoiceNumber: poNumber,
            quantity: quantity.toFixed(2),
            unitPrice: unitPrice.toFixed(2),
            totalPrice: totalPrice.toFixed(2)
          })
        }
      }

      if (exportRows.length === 0) {
        toast({
          title: 'Error',
          description: 'No purchase order lines to export.',
          variant: 'destructive'
        })
        return
      }

      // Helper function to escape CSV cells
      const escapeCSVCell = (value: any): string => {
        if (value === null || value === undefined || value === '') {
          return ''
        }
        let str = String(value).trim()
        if (str === '' || str === 'null' || str === 'undefined') {
          return ''
        }
        // Remove any existing surrounding quotes
        while (str.startsWith('"') && str.endsWith('"') && str.length > 1) {
          str = str.slice(1, -1)
        }
        // Replace double quotes with escaped quotes
        str = str.replace(/"/g, '""')
        // Quote if contains comma, quote, or newline
        if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
          return `"${str}"`
        }
        return str
      }

      // Create CSV content
      const headers = ['Date', 'Supplier', 'Item Name', 'Item category', 'Invoice N./ DMC', 'Quantity', 'Unit price', 'Total price']
      
      const csvContent = [
        headers.map(escapeCSVCell).join(','),
        ...exportRows.map(row => [
          escapeCSVCell(row.date),
          escapeCSVCell(row.supplier),
          escapeCSVCell(row.itemName),
          escapeCSVCell(row.itemCategory),
          escapeCSVCell(row.invoiceNumber),
          escapeCSVCell(row.quantity),
          escapeCSVCell(row.unitPrice),
          escapeCSVCell(row.totalPrice)
        ].join(','))
      ].join('\n')

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.setAttribute('href', url)
      link.setAttribute('download', `purchase-orders-report-${new Date().toISOString().split('T')[0]}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      toast({
        title: 'Success',
        description: `Exported ${exportRows.length} line items to CSV.`,
      })
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to export purchase orders. Please try again.',
        variant: 'destructive'
      })
    }
  }

  /**
   * Handle downloading good receipt PDF
   */
  const handleDownloadGoodReceipt = async (purchaseOrderId: string, poNumber: string) => {
    try {
      const loadingToast = toast({
        title: 'Generating...',
        description: `Generating good receipt for ${poNumber}...`,
      })

      const blob = await purchaseApi.downloadGoodReceiptPDF(purchaseOrderId)
      
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `GoodReceipt-${poNumber}.pdf`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      loadingToast.dismiss()
      toast({
        title: 'Success',
        description: `Good receipt for ${poNumber} downloaded successfully`,
      })

    } catch (error: any) {
      console.error('Error downloading good receipt:', error)
      toast({
        title: 'Error',
        description: error?.message || 'Failed to download good receipt',
        variant: 'destructive'
      })
    }
  }

  if (!isAuthenticated || authLoading) {
    return (
      <PageLayout>
        <div className="flex items-center justify-center h-64">
          <MoonLoader size="lg" color="teal" />
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between bg-gradient-to-r from-cyan-50 to-blue-50 rounded-xl p-6 border border-cyan-100 shadow-sm">
          <div>
            <h1 className="text-4xl font-bold tracking-tight bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent"><TranslatedText text="Purchase Orders" /></h1>
            <p className="text-muted-foreground mt-1 text-lg"><TranslatedText text="Manage your purchase orders and procurement process" /></p>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              onClick={exportPurchaseOrders}
              className="h-9 px-4"
            >
              <Download className="w-4 h-4 mr-2" />
              <TranslatedText text="Export Data" />
            </Button>
            <Button 
              onClick={() => {
                setEditingOrder(null)
                // Always use innovation-hub as the company
                const defaultCompanyId = 'innovation-hub';
                // Generate a unique PO number with timestamp
                const timestamp = new Date().getTime();
                const poNumber = `PO-${timestamp}`;
                
                form.reset({
                  companyId: defaultCompanyId,
                  vendorId: '',
                  poNumber: poNumber,
                  orderDate: new Date().toISOString().split('T')[0],
                  expectedDelivery: '',
                  orderSource: 'external',
                  currency: 'USD',
                  notes: '',
                  lines: [{ description: '', quantity: 1, unitPrice: 0, taxRate: 0 }]
                })
                setIsCreateOpen(true)
              }}
            >
              <Plus className="w-4 h-4 mr-2" />
              <TranslatedText text="New Purchase Order" />
            </Button>
          </div>
        </div>

        {/* Analytics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="text-sm font-medium text-gray-600"><TranslatedText text="Total Orders" /></p>
                  <p className="text-2xl font-bold text-gray-900">{Array.isArray(purchaseOrders) ? purchaseOrders.length : 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2">
                <DollarSign className="w-5 h-5 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-600"><TranslatedText text="Total Value" /></p>
                  <p className="text-lg font-bold text-gray-900 truncate">
                    {Array.isArray(purchaseOrders) ? formatCurrency(purchaseOrders.reduce((sum, order) => sum + Number(order.totalAmount || 0), 0)) : '$0.00'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2">
                <Truck className="w-5 h-5 text-orange-600" />
                <div>
                  <p className="text-sm font-medium text-gray-600"><TranslatedText text="Pending Orders" /></p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Array.isArray(purchaseOrders) ? purchaseOrders.filter(order => ['draft', 'sent'].includes(order.status)).length : 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="text-sm font-medium text-gray-600"><TranslatedText text="Completed Orders" /></p>
                  <p className="text-2xl font-bold text-gray-900">
                    {Array.isArray(purchaseOrders) ? purchaseOrders.filter(order => ['approved', 'delivered'].includes(order.status)).length : 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Purchase Orders Table */}
        <Card>
          <CardHeader>
            <CardTitle><TranslatedText text="Purchase Orders" /></CardTitle>
          </CardHeader>
          <CardContent>
            {/* Filters */}
            <div className="flex items-center space-x-4 mb-6">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder={tSync("Search purchase orders...")}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={tSync("Filter by status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all"><TranslatedText text="All Status" /></SelectItem>
                  <SelectItem value="draft"><TranslatedText text="Draft" /></SelectItem>
                  <SelectItem value="approved"><TranslatedText text="Approved" /></SelectItem>
                  <SelectItem value="delivered"><TranslatedText text="Delivered" /></SelectItem>
                  <SelectItem value="closed"><TranslatedText text="Closed" /></SelectItem>
                  <SelectItem value="cancelled"><TranslatedText text="Cancelled" /></SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Table */}
            {ordersLoading ? (
              <div className="flex items-center justify-center py-8">
                <MoonLoader size="lg" />
              </div>
            ) : (
              <div className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[120px]"><TranslatedText text="PO Number" /></TableHead>
                      <TableHead className="w-[200px]"><TranslatedText text="Vendor" /></TableHead>
                      <TableHead className="w-[100px]"><TranslatedText text="Date" /></TableHead>
                      <TableHead className="w-[120px]"><TranslatedText text="Amount" /></TableHead>
                      <TableHead className="w-[100px]"><TranslatedText text="Status" /></TableHead>
                      <TableHead className="w-[140px]"><TranslatedText text="Inventory Type" /></TableHead>
                      <TableHead className="w-[80px]"><TranslatedText text="Actions" /></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8">
                          <div className="text-gray-500">
                            <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                            <p><TranslatedText text="No purchase orders found" /></p>
                            <p className="text-sm"><TranslatedText text="Create your first purchase order to get started" /></p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayOrders.map((order) => (
                        <TableRow key={order.id}>
                          <TableCell className="font-medium">{order.poNumber}</TableCell>
                          <TableCell className="truncate" title={order.vendor?.name || '-'}>
                            {order.vendor?.name || '-'}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {order.orderDate ? format(new Date(order.orderDate), 'MMM dd') : '-'}
                          </TableCell>
                          <TableCell className="font-medium">{formatCurrency(order.totalAmount)}</TableCell>
                          <TableCell>
                            <Badge 
                              variant={getStatusColor(order.status)} 
                              className={`flex items-center gap-1 w-fit ${getStatusBadgeClassName(order.status)}`}
                            >
                              {getStatusIcon(order.status)}
                              {tSync(order.status.charAt(0).toUpperCase() + order.status.slice(1))}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={order.inventoryType === 'fixed_assets' ? 'secondary' : 'outline'}
                              className="text-xs"
                            >
                              {order.inventoryType === 'fixed_assets' ? <TranslatedText text="Fixed Assets" /> : <TranslatedText text="Inventory" />}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center space-x-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setSelectedOrder(order)
                                  setIsViewOpen(true)
                                }}
                                title={tSync("View Details")}
                                className="h-8 w-8 p-0"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={order.status === 'closed' || order.status === 'cancelled'}
                                onClick={() => {
                                  setEditingOrder(order)
                                  form.reset({
                                    companyId: order.company?.id || companies?.[0]?.id || '',
                                    vendorId: order.vendorId,
                                    poNumber: order.poNumber,
                                    orderDate: order.orderDate ? new Date(order.orderDate).toISOString().split('T')[0] : '',
                                    expectedDelivery: order.expectedDelivery ? new Date(order.expectedDelivery).toISOString().split('T')[0] : '',
                                    orderSource: order.orderSource || 'external',
                                    currency: order.currency || 'USD',
                                    notes: order.notes || '',
                                    lines: order.lines.map(line => ({
                                      id: line.id,
                                      productId: line.productId || '',
                                      description: line.description,
                                      quantity: line.quantity,
                                      unitPrice: line.unitPrice,
                                      taxRate: line.taxRate
                                    }))
                                  })
                                  setIsCreateOpen(true)
                                }}
                                title={order.status === 'closed' || order.status === 'cancelled' ? tSync('Cannot edit closed or cancelled orders') : tSync('Edit Purchase Order')}
                                className="h-8 w-8 p-0"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {order.status === 'draft' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={approvingOrderId === order.id}
                                  onClick={() => handleApproveOrder(order)}
                                  className="h-7 px-2 text-xs bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                                  title={tSync("Approve Purchase Order")}
                                >
                                  {approvingOrderId === order.id ? (
                                    <>
                                      <MoonLoader size="sm" className="mr-1.5" color="green" />
                                      <span className="text-xs"><TranslatedText text="Approving..." /></span>
                                    </>
                                  ) : (
                                    <span className="text-xs"><TranslatedText text="Approve" /></span>
                                  )}
                                </Button>
                              )}
                              {order.status === 'approved' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={deliveringOrderId === order.id || cancellingOrderId === order.id}
                                  onClick={() => handleDeliverOrder(order)}
                                  className="h-7 px-2 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                                  title={tSync("Mark as Delivered")}
                                >
                                  {deliveringOrderId === order.id ? (
                                    <>
                                      <MoonLoader size="sm" className="mr-1.5" color="blue" />
                                      <span className="text-xs"><TranslatedText text="Delivering..." /></span>
                                    </>
                                  ) : (
                                    <span className="text-xs"><TranslatedText text="Delivered" /></span>
                                  )}
                                </Button>
                              )}
                              {order.status === 'draft' && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  disabled={approvingOrderId === order.id || cancellingOrderId === order.id}
                                  onClick={() => handleCancelOrder(order)}
                                  className="h-7 px-2 text-xs bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                                  title={tSync("Cancel Purchase Order")}
                                >
                                  {cancellingOrderId === order.id ? (
                                    <>
                                      <MoonLoader size="sm" className="mr-1.5" color="gray" />
                                      <span className="text-xs"><TranslatedText text="Cancelling..." /></span>
                                    </>
                                  ) : (
                                    <span className="text-xs"><TranslatedText text="Cancel" /></span>
                                  )}
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={order.status !== 'draft'}
                                onClick={() => {
                                  if (confirm(tSync('Are you sure you want to delete this purchase order?'))) {
                                    deleteOrder.mutate(order.id)
                                  }
                                }}
                                title={order.status !== 'draft' ? tSync('Only draft orders can be deleted') : tSync('Delete Purchase Order')}
                                className={order.status !== 'draft' ? 'opacity-50 cursor-not-allowed' : ''}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
          
          {/* Pagination Controls */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-4 border-t">
              <div className="text-sm text-gray-500">
                <TranslatedText text="Showing" /> {((currentPage - 1) * pageSize) + 1} <TranslatedText text="to" /> {Math.min(currentPage * pageSize, pagination.total)} <TranslatedText text="of" /> {pagination.total} <TranslatedText text="results" />
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  <TranslatedText text="Previous" />
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = Math.max(1, Math.min(pagination.totalPages - 4, currentPage - 2)) + i;
                    if (pageNum > pagination.totalPages) return null;
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(pagination.totalPages, prev + 1))}
                  disabled={currentPage === pagination.totalPages}
                >
                  <TranslatedText text="Next" />
                </Button>
              </div>
            </div>
          )}
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateOpen} onOpenChange={(open) => {
          setIsCreateOpen(open)
          if (!open) {
            setEditingOrder(null)
            form.reset()
          }
        }}>
          <DialogContent className="!max-w-7xl max-h-[95vh] overflow-hidden w-[95vw] flex flex-col bg-white">
            <DialogHeader className="pb-6 border-b border-slate-200 bg-white flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="relative">
                    <div className="w-14 h-14 bg-teal-600 rounded-2xl flex items-center justify-center shadow-sm">
                      <Package className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-teal-500 rounded-full border-2 border-white flex items-center justify-center">
                      <Plus className="w-3 h-3 text-white" />
                    </div>
                  </div>
                  <div>
                    <DialogTitle className="text-3xl font-bold text-slate-900">
                  {editingOrder ? (
                    <span>
                      <TranslatedText text="Edit Purchase Order" />
                      {editingOrder.poNumber ? (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">#{editingOrder.poNumber}</span>
                      ) : null}
                    </span>
                  ) : (
                    <TranslatedText text="Create Purchase Order" />
                  )}
                </DialogTitle>
                    <DialogDescription className="text-slate-600 mt-1 text-sm">
                      <TranslatedText text="Professional purchase order tracking • Full audit trail • Real-time calculations" />
                    </DialogDescription>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                {editingOrder ? (
                    <Badge 
                      variant={getStatusColor(editingOrder.status)} 
                      className={`flex items-center gap-1 w-fit ${getStatusBadgeClassName(editingOrder.status)}`}
                    >
                    {getStatusIcon(editingOrder.status)}
                    {tSync(editingOrder.status.charAt(0).toUpperCase() + editingOrder.status.slice(1))}
                  </Badge>
                  ) : (
                    <Badge className="bg-teal-100 text-teal-700 border-teal-200 px-4 py-2 text-sm font-semibold">
                      <TranslatedText text="Draft Mode" />
                    </Badge>
                  )}
                </div>
              </div>
            </DialogHeader>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col" style={{ maxHeight: 'calc(95vh - 180px)' }}>
                <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100 hover:scrollbar-thumb-slate-400">
                  <div className="space-y-6 py-6 px-6 pb-4">
                    {/* Main Form Grid - Two Column Layout */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                      
                      {/* LEFT COLUMN */}
                      <div className="space-y-6">
                        
                        {/* Basic Information Section */}
                        <div className="border border-slate-200 rounded-lg p-5 bg-white">
                          <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <Tag className="w-4 h-4 text-slate-600" />
                            <TranslatedText text="Basic Information" />
                          </h3>
                          <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="companyId"
                              render={({ field }: any) => (
                                <FormItem className="w-full">
                                  <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Company" /> *</FormLabel>
                                  <FormControl>
                                    <Input 
                                      value={activeCompanyName || tSync('No company selected')} 
                                      disabled 
                                      className="h-10 bg-slate-50 border-slate-300 w-full"
                                      readOnly
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                  />
                  
                  <FormField
                    control={form.control}
                              name="poNumber"
                              render={({ field }: any) => (
                                <FormItem className="w-full">
                                  <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="PO Number" /> *</FormLabel>
                          <FormControl>
                                    <Input placeholder={tSync("PO-2024-001")} className="h-10 border-slate-300" {...field} />
                          </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                            <FormField
                              control={form.control}
                              name="orderDate"
                              render={({ field }: any) => (
                                <FormItem className="w-full">
                                  <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Order Date" /> *</FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input 
                                        type="date" 
                                        ref={(e) => {
                                          field.ref(e)
                                          if (e) {
                                            (e as any)._orderDateInput = e
                                          }
                                        }}
                                        className="h-10 pr-10 border-slate-300 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden [&::-ms-clear]:hidden [&::-ms-expand]:hidden" 
                                        {...field} 
                                      />
                                      <Calendar 
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 cursor-pointer hover:text-slate-600" 
                                        onClick={(e) => {
                                          const input = (e.currentTarget.parentElement?.querySelector('input[type="date"]') as HTMLInputElement)
                                          if (input) {
                                            input.showPicker?.()
                                            input.focus()
                                            input.click()
                                          }
                                        }}
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                            
                            <FormField
                              control={form.control}
                              name="expectedDelivery"
                              render={({ field }: any) => (
                                <FormItem className="w-full">
                                  <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Expected Delivery" /></FormLabel>
                                  <FormControl>
                                    <div className="relative">
                                      <Input 
                                        type="date" 
                                        ref={(e) => {
                                          field.ref(e)
                                          if (e) {
                                            (e as any)._expectedDeliveryInput = e
                                          }
                                        }}
                                        className="h-10 pr-10 border-slate-300 [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none [&::-webkit-inner-spin-button]:hidden [&::-webkit-outer-spin-button]:hidden [&::-ms-clear]:hidden [&::-ms-expand]:hidden" 
                                        {...field} 
                                      />
                                      <Calendar 
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400 cursor-pointer hover:text-slate-600" 
                                        onClick={(e) => {
                                          const input = (e.currentTarget.parentElement?.querySelector('input[type="date"]') as HTMLInputElement)
                                          if (input) {
                                            input.showPicker?.()
                                            input.focus()
                                            input.click()
                                          }
                                        }}
                                      />
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </div>
                        </div>

                        {/* Vendor & Organization Section */}
                        <div className="border border-slate-200 rounded-lg p-5 bg-white">
                          <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <Building className="w-4 h-4 text-slate-600" />
                            <TranslatedText text="Vendor & Organization" />
                          </h3>
                          <div className="space-y-4">
                  <FormField
                    control={form.control}
                              name="vendorId"
                              render={({ field }: any) => (
                                <FormItem className="w-full">
                                  <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Vendor" /> *</FormLabel>
                                  <Select 
                                    value={field.value} 
                                    onValueChange={(value) => {
                                      if (value === '__create_new__') {
                                        setIsVendorModalOpen(true)
                                      } else {
                                        field.onChange(value)
                                      }
                                    }}
                                  >
                        <FormControl>
                                      <SelectTrigger className="h-10 border-slate-300 w-full">
                                        <SelectValue placeholder={tSync("Select vendor")} />
                                      </SelectTrigger>
                        </FormControl>
                                    <SelectContent>
                                      <SelectItem 
                                        value="__create_new__"
                                        className="text-primary font-medium cursor-pointer"
                                      >
                                        <div className="flex items-center gap-2">
                                          <Plus className="w-4 h-4 text-primary" />
                                          <span><TranslatedText text="Create New Vendor" /></span>
                                        </div>
                                      </SelectItem>
                                      {(vendors || []).map((vendor: any) => (
                                        <SelectItem key={vendor.id} value={vendor.id}>
                                          <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 text-xs font-medium">
                                              {vendor.name?.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                              <div className="font-medium">{vendor.name}</div>
                                              {vendor.email && (
                                                <div className="text-xs text-slate-500">{vendor.email}</div>
                                              )}
                                            </div>
                                          </div>
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  
                            <div className="grid grid-cols-2 gap-4 w-full">
                  <FormField
                    control={form.control}
                    name="orderSource"
                                render={({ field }: any) => (
                                  <FormItem className="w-full">
                                    <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Order Source" /></FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                                        <SelectTrigger className="h-10 border-slate-300 w-full">
                              <SelectValue placeholder={tSync("Select order source")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="internal"><TranslatedText text="Internal" /></SelectItem>
                            <SelectItem value="external"><TranslatedText text="External" /></SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="inventoryType"
                                render={({ field }: any) => (
                                  <FormItem className="w-full">
                                    <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Inventory Type" /></FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                                        <SelectTrigger className="h-10 border-slate-300 w-full">
                              <SelectValue placeholder={tSync("Select inventory type")} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                                        <SelectItem value="assets"><TranslatedText text="Inventory" /></SelectItem>
                                        <SelectItem value="fixed_assets"><TranslatedText text="Fixed Assets" /></SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* RIGHT COLUMN */}
                      <div className="space-y-6">
                        
                        {/* Financial Details Section */}
                        <div className="border border-slate-200 rounded-lg p-5 bg-white">
                          <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <DollarSign className="w-4 h-4 text-slate-600" />
                            <TranslatedText text="Financial Details" />
                          </h3>
                          <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="currency"
                              render={({ field }: any) => (
                                <FormItem className="w-full">
                                  <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Currency" /></FormLabel>
                        <Select value={field.value} onValueChange={field.onChange}>
                          <FormControl>
                                      <SelectTrigger className="h-10 border-slate-300 w-full">
                                        <SelectValue placeholder="Frw" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                                      <SelectItem value="Frw">Frw</SelectItem>
                            <SelectItem value="USD">USD</SelectItem>
                            <SelectItem value="EUR">EUR</SelectItem>
                            <SelectItem value="GBP">GBP</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                  </div>
                  
                        {/* Payment & Accounting Section */}
                        <div className="border border-slate-200 rounded-lg p-5 bg-white">
                          <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-slate-600" />
                            <TranslatedText text="Line Items" />
                          </h3>
                          <div className="space-y-4">
                            <div className="max-h-[400px] overflow-y-auto space-y-3 pr-2">
                  {form.watch('lines').map((_, index) => (
                                <div key={`${index}-${currentInventoryType}`} className="border border-slate-200 rounded-lg p-4 bg-white">
                                  <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name={`lines.${index}.productId`}
                                      render={({ field }: any) => (
                                        <FormItem className="w-full">
                                          <FormLabel className="text-sm font-medium text-slate-700">{currentInventoryType === 'fixed_assets' ? <TranslatedText text="Asset" /> : <TranslatedText text="Product" />}</FormLabel>
                            <div className="flex gap-2 items-start">
                              <Select 
                                value={currentInventoryType === 'fixed_assets' ? form.watch(`lines.${index}.description`) || '' : (field.value || '')} 
                                onValueChange={(value) => {
                                  if (currentInventoryType === 'fixed_assets') {
                                    // For fixed assets, we don't set productId (it should be null)
                                    // Instead, we store the asset info in description and unitPrice
                                    field.onChange(''); // Clear productId for fixed assets
                                    const asset = fixedAssets?.find(a => a.id === value);
                                    if (asset) {
                                      form.setValue(`lines.${index}.description`, asset.name);
                                      form.setValue(`lines.${index}.unitPrice`, asset.cost || 0);
                                      // Explicitly clear productId to ensure it's not set
                                      form.setValue(`lines.${index}.productId`, '');
                                    }
                                  } else {
                                    // For regular products, set the productId normally
                                    // First update the field value to ensure Select component reflects the change immediately
                                    field.onChange(value);
                                    
                                    // Then find and update related fields synchronously
                                    const product = products?.find(p => p.id === value);
                                    if (product) {
                                      // Update description and unitPrice immediately after productId
                                      form.setValue(`lines.${index}.description`, product.name, { shouldValidate: true, shouldDirty: true });
                                      form.setValue(`lines.${index}.unitPrice`, product.unitPrice || 0, { shouldValidate: true, shouldDirty: true });
                                    }
                                  }
                                }}
                              >
                                <FormControl>
                                                <SelectTrigger className="h-10 border-slate-300 w-full">
                                    <SelectValue placeholder={currentInventoryType === 'fixed_assets' ? tSync("Select asset") : tSync("Select product")}>
                                      {(() => {
                                        if (currentInventoryType === 'fixed_assets') {
                                          // For fixed assets, show the description as the selected value
                                          const description = form.watch(`lines.${index}.description`);
                                          return description || tSync("Select asset");
                                        } else {
                                          // For regular products, use productId to find the product
                                          // Use field.value (which is reactive) instead of form.getValues()
                                          const selectedId = field.value;
                                          const selectedProduct = products?.find(p => p.id === selectedId);
                                          return selectedProduct ? selectedProduct.name : (selectedId ? tSync("Product not found") : tSync("Select product"));
                                        }
                                      })()}
                                    </SelectValue>
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent className="max-h-60 overflow-y-auto">
                                  {(() => {
                                    const itemsToShow = currentInventoryType === 'fixed_assets' ? fixedAssets : products;
                                    
                                    if (!itemsToShow || itemsToShow.length === 0) {
                                      return (
                                        <SelectItem value="__no_items__" disabled>
                                          {currentInventoryType === 'fixed_assets' ? <TranslatedText text="No assets available" /> : <TranslatedText text="No products available" />}
                                        </SelectItem>
                                      );
                                    }
                                    
                                    return itemsToShow.map((item) => (
                                      <SelectItem 
                                        key={item.id} 
                                        value={item.id}
                                        disabled={currentInventoryType === 'assets' ? item.status === 'DISCONTINUED' : item.status === 'DISPOSED'}
                                      >
                                        <div>
                                          <div className="font-semibold">{item.name}</div>
                                          <div className="text-xs text-muted-foreground">
                                            {currentInventoryType === 'fixed_assets' 
                                              ? <><TranslatedText text="Asset" /> - {formatCurrency(item.cost || 0)}{item.quantity ? <> (<TranslatedText text="Qty" />: {item.quantity})</> : ''}</>
                                              : <>{item.stockQuantity || 0} <TranslatedText text="in stock" />{item.status === 'INACTIVE' ? <> (<TranslatedText text="Inactive" />)</> : ''}</>
                                            }
                                          </div>
                                        </div>
                                      </SelectItem>
                                    ));
                                  })()}
                                </SelectContent>
                              </Select>
                            </div>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name={`lines.${index}.description`}
                                          render={({ field }: any) => (
                                            <FormItem className="w-full">
                                              <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Description" /></FormLabel>
                            <FormControl>
                                                <Input placeholder={tSync("Item description")} className="h-10 border-slate-300" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`lines.${index}.quantity`}
                                          render={({ field }: any) => (
                                            <FormItem className="w-full">
                                              <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Quantity" /></FormLabel>
                            <FormControl>
                                                <Input type="number" placeholder="1" className="h-10 border-slate-300" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`lines.${index}.unitPrice`}
                                          render={({ field }: any) => (
                                            <FormItem className="w-full">
                                              <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Unit Price" /></FormLabel>
                            <FormControl>
                                                <Input type="number" step="0.01" placeholder="0.00" className="h-10 border-slate-300" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`lines.${index}.taxRate`}
                                          render={({ field }: any) => (
                                            <FormItem className="w-full">
                                              <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Tax Rate (%)" /></FormLabel>
                            <FormControl>
                                                <Input type="number" step="0.01" placeholder="0" className="h-10 border-slate-300" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                                        <div className="flex items-center justify-between">
                                          <div className="text-sm text-slate-600">
                                            <TranslatedText text="Line Total" />: <span className="font-semibold">
                            {(() => {
                              const l = (form.getValues('lines') as any[])[index] || {}
                              const qty = Number(l?.quantity || 0)
                              const price = Number(l?.unitPrice || 0)
                              const base = qty * price
                              const tax = base * (Number(l?.taxRate || 0) / 100)
                              return formatCurrency(base + tax)
                            })()}
                                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const lines = form.getValues('lines')
                              if (lines.length > 1) {
                                form.setValue('lines', lines.filter((_, i) => i !== index))
                              }
                            }}
                                            className="h-8 w-8 p-0"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                  
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const lines = form.getValues('lines')
                      form.setValue('lines', [...lines, { description: '', quantity: 1, unitPrice: 0, taxRate: 0 }])
                    }}
                                    className="w-full mt-3"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    <TranslatedText text="Add Line Item" />
                  </Button>
                                </div>
                          </div>
                </div>

                        {/* Notes Section */}
                        <div className="border border-slate-200 rounded-lg p-5 bg-white">
                          <h3 className="text-base font-semibold text-slate-900 mb-4 flex items-center gap-2">
                            <FileText className="w-4 h-4 text-slate-600" />
                            <TranslatedText text="Notes" />
                          </h3>
                          <FormField
                            control={form.control}
                            name="notes"
                            render={({ field }: any) => (
                              <FormItem>
                                <FormLabel className="text-sm font-medium text-slate-700"><TranslatedText text="Additional Notes" /></FormLabel>
                                <FormControl>
                                  <Textarea 
                                    placeholder={tSync("Add any additional notes about this purchase order...")} 
                                    className="min-h-[100px] resize-none border-slate-300" 
                                    {...field} 
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                  </div>
                  </div>
                  </div>
                    
                    {/* Order Summary - Full Width */}
                    <div className="border-t border-slate-200 bg-slate-50 pt-4 px-6 pb-6 mt-6">
                      <div className="w-full">
                        <div className="bg-white border-2 border-slate-200 rounded-lg p-6 shadow-sm">
                          <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
                            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
                              <Calculator className="w-4 h-4 text-slate-600" />
                              <TranslatedText text="Order Summary" />
                            </h3>
                            <Badge variant="outline" className="text-xs">
                              <TranslatedText text="Preview" />
                            </Badge>
                </div>

                          <div className="space-y-3">
                            <div className="flex items-center justify-between py-2">
                              <span className="text-sm text-slate-600"><TranslatedText text="Subtotal" /></span>
                              <span className="text-base font-medium text-slate-900">
                                {formatCurrency(totals.subtotal)}
                              </span>
                            </div>
                            
                            {totals.tax > 0 && (
                              <div className="flex items-center justify-between py-2">
                                <span className="text-sm text-slate-600"><TranslatedText text="Tax" /></span>
                                <span className="text-base font-medium text-slate-700">
                                  +{formatCurrency(totals.tax)}
                                </span>
                              </div>
                            )}
                            
                            <div className="flex items-center justify-between pt-3 mt-2 border-t-2 border-slate-200">
                              <span className="text-base font-semibold text-slate-900"><TranslatedText text="Total Order" /></span>
                              <div className="text-right">
                                <div className="text-2xl font-bold text-slate-900">
                                  {formatCurrency(totals.total)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex-shrink-0 bg-white border-t-2 border-slate-200 p-6 shadow-sm">
                  <div className="flex items-center justify-between w-full">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setIsCreateOpen(false)}
                      disabled={createOrder.isPending || updateOrder.isPending}
                      className="h-14 px-8 text-base font-semibold border-2 border-slate-300 hover:bg-slate-100 transition-all duration-200"
                    >
                    <TranslatedText text="Cancel" />
                  </Button>
                    <Button 
                      type="submit" 
                      disabled={createOrder.isPending || updateOrder.isPending}
                      className="h-14 px-10 text-base font-semibold bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md transition-all duration-200"
                    >
                      {createOrder.isPending || updateOrder.isPending ? (
                        <>
                          <MoonLoader size="sm" className="mr-2" color="teal" />
                          {editingOrder ? <TranslatedText text="Updating..." /> : <TranslatedText text="Creating..." />}
                        </>
                      ) : (
                        <>
                          <CheckCircle className="w-5 h-5 mr-2" />
                          {editingOrder ? <TranslatedText text="Update Order" /> : <TranslatedText text="Create Order" />}
                        </>
                      )}
                  </Button>
                  </div>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>

        {/* View Dialog */}
        <Dialog open={isViewOpen} onOpenChange={(open) => {
          setIsViewOpen(open)
          if (!open) {
            setSelectedOrder(null)
          }
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle><TranslatedText text="Purchase Order Details" /></DialogTitle>
            </DialogHeader>
            {orderDetailsLoading ? (
              <div className="flex items-center justify-center py-8">
                <MoonLoader size="md" color="teal" />
              </div>
            ) : orderDetails ? (
              <div className="space-y-6">
                {/* Basic Information */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500"><TranslatedText text="PO Number" /></label>
                    <p className="text-lg font-medium">{orderDetails.poNumber}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500"><TranslatedText text="Vendor" /></label>
                    <p className="text-lg">{orderDetails.vendor?.name || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500"><TranslatedText text="Order Date" /></label>
                    <p className="text-lg">{orderDetails.orderDate ? format(new Date(orderDetails.orderDate), 'MMM dd, yyyy') : '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500"><TranslatedText text="Expected Delivery" /></label>
                    <p className="text-lg">
                      {orderDetails.expectedDelivery ? format(new Date(orderDetails.expectedDelivery), 'MMM dd, yyyy') : '-'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500"><TranslatedText text="Total Amount" /></label>
                    <p className="text-lg font-bold text-green-600">{formatCurrency(orderDetails.totalAmount)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500"><TranslatedText text="Status" /></label>
                    <div className="mt-1">
                      <Badge 
                        variant={getStatusColor(orderDetails.status)} 
                        className={`flex items-center gap-1 w-fit ${getStatusBadgeClassName(orderDetails.status)}`}
                      >
                        {getStatusIcon(orderDetails.status)}
                        {tSync(orderDetails.status.charAt(0).toUpperCase() + orderDetails.status.slice(1))}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* Notes */}
                {orderDetails.notes && (
                  <div className="space-y-4">
                    <Separator />
                    <h3 className="text-lg font-medium"><TranslatedText text="Notes" /></h3>
                    <div className="space-y-3">
                      {formatPurchaseOrderNotes(orderDetails.notes, tSync)}
                    </div>
                  </div>
                )}

                {/* Line Items */}
                <div className="space-y-4">
                  <Separator />
                  <h3 className="text-lg font-medium"><TranslatedText text="Line Items" /></h3>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead><TranslatedText text="Description" /></TableHead>
                          <TableHead><TranslatedText text="Quantity" /></TableHead>
                          <TableHead><TranslatedText text="Unit Price" /></TableHead>
                          <TableHead><TranslatedText text="Tax Rate" /></TableHead>
                          <TableHead><TranslatedText text="Total" /></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orderDetails.lines.map((line) => (
                          <TableRow key={line.id}>
                            <TableCell>{line.description}</TableCell>
                            <TableCell>{line.quantity}</TableCell>
                            <TableCell>{formatCurrency(line.unitPrice)}</TableCell>
                            <TableCell>{line.taxRate}%</TableCell>
                            <TableCell>{formatCurrency(line.lineTotal || (line.quantity * line.unitPrice))}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Timestamps */}
                <div className="space-y-4">
                  <Separator />
                  <h3 className="text-lg font-medium"><TranslatedText text="Timestamps" /></h3>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="text-sm font-medium text-gray-500"><TranslatedText text="Created" /></label>
                      <p className="text-lg">{orderDetails.createdAt ? format(new Date(orderDetails.createdAt), 'MMM dd, yyyy HH:mm') : '-'}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500"><TranslatedText text="Last Updated" /></label>
                      <p className="text-lg">{orderDetails.updatedAt ? format(new Date(orderDetails.updatedAt), 'MMM dd, yyyy HH:mm') : '-'}</p>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500"><TranslatedText text="No purchase order details found" /></p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Vendor Modal */}
        <VendorModal
          isOpen={isVendorModalOpen}
          onClose={() => setIsVendorModalOpen(false)}
          onSubmit={async (vendorData) => {
            await createVendorMutation.mutateAsync(vendorData)
          }}
          companyId={companyId}
          isLoading={createVendorMutation.isPending}
        />
      </div>
    </PageLayout>
  )
}
